import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    HStack,
    Icon,
    IconButton,
    Image,
    Input,
    Menu,
    MenuButton,
    MenuItemOption,
    MenuList,
    MenuOptionGroup,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Progress,
    Select,
    SimpleGrid,
    Spinner,
    Stack,
    Switch,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    Textarea,
    useColorModeValue,
    useToast,
    VisuallyHidden,
    Wrap,
    WrapItem,
} from "@chakra-ui/react";
import {CheckIcon, ChevronDownIcon, CloseIcon, DeleteIcon} from "@chakra-ui/icons";
import React, {useEffect, useRef, useState} from "react";
import {FiBookOpen, FiGlobe, FiGrid, FiImage, FiMapPin, FiPhone, FiSettings, FiUploadCloud} from "react-icons/fi";
import {fileUpload} from "../../../Services/FileService";
import {fetchActiveProfileMenus} from "./Services";

const initialSchoolDetails = {
    name: "",
    address: "",
    email: "",
    phone: "",
    website: "",
    type: "",
    subtype: "",
    students: "",
    classes: "",
    speakersNeeded: false,
    description: "",
    logo: null,
    logoPreview: null,
    maxBells: "",
    maxRosters: "",
    profileMenuCodes: [],
};

const requiredFields = ["name", "address", "email", "phone", "type", "subtype", "students", "classes", "maxBells"];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;

// Map a school detail response (backend field names) to the modal's form shape.
const mapSchoolToForm = (school) => ({
    name: school?.name || "",
    address: school?.address || "",
    email: school?.email || "",
    phone: school?.phone || "",
    website: school?.website || "",
    type: school?.type || "",
    subtype: school?.subType || "",
    students: school?.totalStudent ?? "",
    classes: school?.totalClass ?? "",
    speakersNeeded: Boolean(school?.speakersNeeded),
    description: school?.description || "",
    logo: school?.logo || null,
    logoPreview: school?.logo || null,
    banner: school?.banner || null,
    maxBells: school?.maxBell ?? "",
    maxRosters: school?.maxRoster ?? "",
    profileMenuCodes: Array.isArray(school?.profileMenuCodes) ? school.profileMenuCodes : [],
});

function AddSchoolModal({isOpen, onClose, handleSchoolSubmit, isEdit = false, initialData = null}) {
    const toast = useToast();
    const fileInputRef = useRef(null);

    const modalBg = useColorModeValue("white", "gray.800");
    const sectionBg = useColorModeValue("gray.50", "gray.700");
    const softBg = useColorModeValue("blue.50", "whiteAlpha.100");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
    const mutedText = useColorModeValue("gray.500", "gray.400");
    const inputBg = useColorModeValue("white", "gray.800");

    const [schoolDetails, setSchoolDetails] = useState(() =>
        isEdit && initialData ? mapSchoolToForm(initialData) : initialSchoolDetails
    );
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Services (profile menus) available for selection — fetched from the backend.
    const [profileMenus, setProfileMenus] = useState([]);
    const [isLoadingProfileMenus, setIsLoadingProfileMenus] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSchoolDetails(initialSchoolDetails);
            setErrors({});
            setTouched({});
            setIsUploadingLogo(false);
            setIsSubmitting(false);
        }
    }, [isOpen]);

    // Load the active profile menus whenever the modal opens.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        (async () => {
            setIsLoadingProfileMenus(true);
            try {
                const response = await fetchActiveProfileMenus();
                if (!cancelled) setProfileMenus(response?.data || []);
            } catch (e) {
                console.error("Error while fetching profile menus e:", e);
                if (!cancelled) setProfileMenus([]);
            } finally {
                if (!cancelled) setIsLoadingProfileMenus(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    useEffect(() => {
        return () => {
            if (schoolDetails.logoPreview) {
                URL.revokeObjectURL(schoolDetails.logoPreview);
            }
        };
    }, [schoolDetails.logoPreview]);

    const validateField = (name, value) => {
        const normalizedValue = typeof value === "string" ? value.trim() : value;

        if (requiredFields.includes(name) && !normalizedValue) {
            return "This field is required.";
        }

        if (name === "email" && normalizedValue && !emailPattern.test(normalizedValue)) {
            return "Enter a valid email address.";
        }

        if (name === "phone" && normalizedValue) {
            const phoneDigits = normalizedValue.replace(/\D/g, "");
            if (phoneDigits.length < 7 || phoneDigits.length > 15) {
                return "Enter a valid phone number.";
            }
        }

        if (name === "website" && normalizedValue && !urlPattern.test(normalizedValue)) {
            return "Enter a valid website URL.";
        }

        if (["students", "classes", "maxBells", "maxRosters"].includes(name) && normalizedValue) {
            const numericValue = Number(normalizedValue);
            if (!Number.isInteger(numericValue) || numericValue < 1) {
                return "Enter a number greater than 0.";
            }
        }

        return "";
    };

    const validateForm = () => {
        const nextErrors = Object.keys(schoolDetails).reduce((acc, key) => {
            const error = validateField(key, schoolDetails[key]);
            if (error) {
                acc[key] = error;
            }
            return acc;
        }, {});

        setErrors(nextErrors);
        setTouched(Object.keys(schoolDetails).reduce((acc, key) => ({...acc, [key]: true}), {}));
        return Object.keys(nextErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setSchoolDetails((prev) => ({...prev, [name]: value}));

        if (touched[name] || errors[name]) {
            const error = validateField(name, value);
            setErrors((prev) => ({...prev, [name]: error}));
        }
    };

    const handleBlur = (e) => {
        const {name, value} = e.target;
        setTouched((prev) => ({...prev, [name]: true}));
        setErrors((prev) => ({...prev, [name]: validateField(name, value)}));
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith("image/")) {
            toast({
                title: "Invalid file",
                description: "Please upload an image file for the school logo.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            toast({
                title: "Logo is too large",
                description: "Please upload an image smaller than 2 MB.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        setIsUploadingLogo(true);

        try {
            const fileUrl = await fileUpload(file, "school");
            const previewUrl = URL.createObjectURL(file);

            setSchoolDetails((prev) => {
                if (prev.logoPreview) {
                    URL.revokeObjectURL(prev.logoPreview);
                }

                return {
                    ...prev,
                    logo: fileUrl,
                    logoPreview: previewUrl,
                };
            });

            toast({
                title: "Logo uploaded",
                description: "The logo is ready to use.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "Upload failed",
                description: "Failed to upload logo. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsUploadingLogo(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const removeLogo = () => {
        setSchoolDetails((prev) => {
            if (prev.logoPreview) {
                URL.revokeObjectURL(prev.logoPreview);
            }

            return {
                ...prev,
                logo: null,
                logoPreview: null,
            };
        });
    };

    // Multi-select of services. MenuOptionGroup(type="checkbox") hands back the
    // full array of selected codes on every toggle.
    const handleProfileMenusChange = (codes) => {
        const next = Array.isArray(codes) ? codes : [codes];
        setSchoolDetails((prev) => ({...prev, profileMenuCodes: next}));
    };

    const removeProfileMenu = (code) => {
        setSchoolDetails((prev) => ({
            ...prev,
            profileMenuCodes: prev.profileMenuCodes.filter((c) => c !== code),
        }));
    };

    const profileMenuLabel = (code) => {
        const match = profileMenus.find((m) => (m.value ?? m.code) === code);
        return match ? (match.label ?? match.name ?? code) : code;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast({
                title: "Check required fields",
                description: "Please fix the highlighted fields before saving.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        setIsSubmitting(true);

        try {
            await handleSchoolSubmit({
                ...schoolDetails,
                name: schoolDetails.name.trim(),
                address: schoolDetails.address.trim(),
                email: schoolDetails.email.trim(),
                phone: schoolDetails.phone.trim(),
                website: schoolDetails.website.trim(),
                students: Number(schoolDetails.students),
                classes: Number(schoolDetails.classes),
                maxBells: Number(schoolDetails.maxBells),
                maxRosters: schoolDetails.maxRosters ? Number(schoolDetails.maxRosters) : "",
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "Error!",
                description: "Failed to save school details. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const completedRequiredFields = requiredFields.filter((field) => !validateField(field, schoolDetails[field])).length;
    const completionPercent = Math.round((completedRequiredFields / requiredFields.length) * 100);

    const textInputProps = {
        bg: inputBg,
        borderColor,
        _hover: {borderColor: "blue.300"},
        _focus: {borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)"}
    };

    const renderField = ({name, label, placeholder, type = "text", icon, helper}) => (
        <FormControl isRequired={requiredFields.includes(name)} isInvalid={Boolean(touched[name] && errors[name])}>
            <FormLabel fontSize="sm" fontWeight="semibold">{label}</FormLabel>
            <Flex align="center" position="relative">
                {icon && (
                    <Icon
                        as={icon}
                        color={mutedText}
                        position="absolute"
                        left="12px"
                        zIndex="1"
                    />
                )}
                <Input
                    name={name}
                    value={schoolDetails[name]}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    type={type}
                    pl={icon ? "38px" : undefined}
                    {...textInputProps}
                />
            </Flex>
            {helper && !errors[name] && <FormHelperText>{helper}</FormHelperText>}
            <FormErrorMessage>{errors[name]}</FormErrorMessage>
        </FormControl>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.600"/>
            <ModalContent bg={modalBg} borderRadius="18px" overflow="hidden">
                <Box bg="linear-gradient(135deg, #1A73E8 0%, #19A7CE 100%)" color="white">
                    <ModalHeader px={{base: 5, md: 8}} py={4}>
                        <Flex direction={{base: "column", md: "row"}} gap={{base: 3, md: 6}} align={{base: "flex-start", md: "center"}} justify="space-between">
                            <HStack spacing={3} align="center" flex="1" minW={0}>
                                <Flex
                                    align="center"
                                    justify="center"
                                    w="40px"
                                    h="40px"
                                    flexShrink={0}
                                    borderRadius="12px"
                                    bg="whiteAlpha.250"
                                >
                                    <Icon as={FiBookOpen} boxSize={5}/>
                                </Flex>
                                <Box minW={0}>
                                    <HStack spacing={2} align="center">
                                        <Text fontSize={{base: "lg", md: "xl"}} fontWeight="bold" noOfLines={1}>
                                            {isEdit ? "Edit School" : "Add New School"}
                                        </Text>
                                        <Badge colorScheme="cyan" variant="solid" borderRadius="full" px={2}>
                                            Admin setup
                                        </Badge>
                                    </HStack>
                                    <Text fontSize="xs" opacity={0.9} noOfLines={1}>
                                        {isEdit
                                            ? "Update the school profile, contact details, and bell capacity."
                                            : "Create the school profile, contact details, and bell capacity in one flow."}
                                    </Text>
                                </Box>
                            </HStack>
                            <Box w={{base: "100%", md: "200px"}} flexShrink={0} pr={{base: 0, md: 6}}>
                                <Flex justify="space-between" fontSize="xs" mb={1}>
                                    <Text>Required details</Text>
                                    <Text fontWeight="bold">{completionPercent}%</Text>
                                </Flex>
                                <Progress value={completionPercent} size="sm" borderRadius="full" bg="whiteAlpha.300" colorScheme="green"/>
                            </Box>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton color="white"/>
                </Box>

                <ModalBody px={{base: 4, md: 8}} py={6}>
                    <Stack spacing={6}>
                        <Box borderWidth="1px" borderColor={borderColor} borderRadius="14px" p={{base: 4, md: 5}} bg={sectionBg}>
                            <Flex align="center" justify="space-between" mb={5} gap={3} wrap="wrap">
                                <Box>
                                    <Text fontWeight="bold" color="gray.700">School identity</Text>
                                    <Text fontSize="sm" color={mutedText}>Basic information visible across the dashboard.</Text>
                                </Box>
                                <Icon as={FiGlobe} color="blue.400" boxSize={5}/>
                            </Flex>

                            <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                                {renderField({
                                    name: "name",
                                    label: "School Name",
                                    placeholder: "Enter school name",
                                    icon: FiBookOpen,
                                })}
                                {renderField({
                                    name: "address",
                                    label: "School Address",
                                    placeholder: "Enter school address",
                                    icon: FiMapPin,
                                })}
                                {renderField({
                                    name: "email",
                                    label: "School Email",
                                    placeholder: "school@example.com",
                                    type: "email",
                                    icon: FiGlobe,
                                })}
                                {renderField({
                                    name: "phone",
                                    label: "Phone Number",
                                    placeholder: "Enter contact number",
                                    type: "tel",
                                    icon: FiPhone,
                                })}
                                {renderField({
                                    name: "website",
                                    label: "School Website",
                                    placeholder: "https://school.edu",
                                    type: "url",
                                    icon: FiGlobe,
                                    helper: "Optional, but useful for profile context.",
                                })}

                                <FormControl isRequired isInvalid={Boolean(touched.type && errors.type)}>
                                    <FormLabel fontSize="sm" fontWeight="semibold">Organization Type</FormLabel>
                                    <Select
                                        name="type"
                                        placeholder="Select organization type"
                                        value={schoolDetails.type}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        bg={inputBg}
                                        borderColor={borderColor}
                                    >
                                        <option value="School">School</option>
                                        <option value="College">College</option>
                                        <option value="Office">Office</option>
                                        <option value="Others">Others</option>
                                    </Select>
                                    <FormErrorMessage>{errors.type}</FormErrorMessage>
                                </FormControl>

                                <FormControl isRequired isInvalid={Boolean(touched.subtype && errors.subtype)}>
                                    <FormLabel fontSize="sm" fontWeight="semibold">School Sub Type</FormLabel>
                                    <Select
                                        name="subtype"
                                        placeholder="Select sub type"
                                        value={schoolDetails.subtype}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        bg={inputBg}
                                        borderColor={borderColor}
                                    >
                                        <option value="PRIVATE">Private</option>
                                        <option value="PUBLIC">Public</option>
                                        <option value="COMMUNITY">Community</option>
                                        <option value="OTHERS">Others</option>
                                    </Select>
                                    <FormErrorMessage>{errors.subtype}</FormErrorMessage>
                                </FormControl>
                            </SimpleGrid>
                        </Box>

                        <SimpleGrid columns={{base: 1, lg: 2}} spacing={5}>
                            <Box borderWidth="1px" borderColor={borderColor} borderRadius="14px" p={{base: 4, md: 5}} bg={sectionBg}>
                                <Flex align="center" justify="space-between" mb={5}>
                                    <Box>
                                        <Text fontWeight="bold" color="gray.700">Capacity setup</Text>
                                        <Text fontSize="sm" color={mutedText}>Enrollment, classes, and bell limits.</Text>
                                    </Box>
                                    <Icon as={FiSettings} color="blue.400" boxSize={5}/>
                                </Flex>

                                <Stack spacing={4}>
                                    <SimpleGrid columns={{base: 1, sm: 2}} spacing={4}>
                                        {renderField({
                                            name: "students",
                                            label: "Students",
                                            placeholder: "e.g. 500",
                                            type: "number",
                                        })}
                                        {renderField({
                                            name: "classes",
                                            label: "Classes",
                                            placeholder: "e.g. 20",
                                            type: "number",
                                        })}
                                        {renderField({
                                            name: "maxBells",
                                            label: "Maximum Bells",
                                            placeholder: "e.g. 40",
                                            type: "number",
                                        })}
                                        {renderField({
                                            name: "maxRosters",
                                            label: "Maximum Rosters",
                                            placeholder: "Optional",
                                            type: "number",
                                        })}
                                    </SimpleGrid>

                                    <Flex
                                        align={{base: "flex-start", sm: "center"}}
                                        justify="space-between"
                                        gap={4}
                                        p={4}
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                        borderRadius="12px"
                                        bg={inputBg}
                                    >
                                        <Box>
                                            <Text fontSize="sm" fontWeight="semibold">Speakers Needed</Text>
                                            <Text fontSize="xs" color={mutedText}>Mark if installation requires speaker support.</Text>
                                        </Box>
                                        <Switch
                                            colorScheme="blue"
                                            isChecked={schoolDetails.speakersNeeded}
                                            onChange={(e) =>
                                                setSchoolDetails((prev) => ({
                                                    ...prev,
                                                    speakersNeeded: e.target.checked,
                                                }))
                                            }
                                        />
                                    </Flex>
                                </Stack>
                            </Box>

                            <Box borderWidth="1px" borderColor={borderColor} borderRadius="14px" p={{base: 4, md: 5}} bg={sectionBg}>
                                <Flex align="center" justify="space-between" mb={5}>
                                    <Box>
                                        <Text fontWeight="bold" color="gray.700">Brand and notes</Text>
                                        <Text fontSize="sm" color={mutedText}>Add logo and a short profile summary.</Text>
                                    </Box>
                                    <Icon as={FiImage} color="blue.400" boxSize={5}/>
                                </Flex>

                                <Stack spacing={4}>
                                    <Box
                                        borderWidth="1px"
                                        borderStyle="dashed"
                                        borderColor={schoolDetails.logoPreview ? "blue.300" : borderColor}
                                        borderRadius="14px"
                                        bg={schoolDetails.logoPreview ? softBg : inputBg}
                                        p={4}
                                    >
                                        <Flex gap={4} align={{base: "flex-start", sm: "center"}} direction={{base: "column", sm: "row"}}>
                                            <Flex
                                                w="96px"
                                                h="96px"
                                                align="center"
                                                justify="center"
                                                borderRadius="14px"
                                                bg={inputBg}
                                                borderWidth="1px"
                                                borderColor={borderColor}
                                                overflow="hidden"
                                            >
                                                {schoolDetails.logoPreview ? (
                                                    <Image
                                                        src={schoolDetails.logoPreview}
                                                        alt="School logo preview"
                                                        w="100%"
                                                        h="100%"
                                                        objectFit="cover"
                                                    />
                                                ) : (
                                                    <Icon as={FiUploadCloud} color="blue.400" boxSize={8}/>
                                                )}
                                            </Flex>

                                            <Box flex="1">
                                                <Text fontSize="sm" fontWeight="semibold">
                                                    {schoolDetails.logoPreview ? "Logo uploaded" : "Upload school logo"}
                                                </Text>
                                                <Text fontSize="xs" color={mutedText} mt={1}>
                                                    PNG, JPG, or WEBP. Maximum 2 MB.
                                                </Text>
                                                <HStack mt={3} spacing={2}>
                                                    <Button
                                                        size="sm"
                                                        leftIcon={schoolDetails.logoPreview ? <CheckIcon/> : <FiUploadCloud/>}
                                                        colorScheme="blue"
                                                        variant={schoolDetails.logoPreview ? "outline" : "solid"}
                                                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                                        isLoading={isUploadingLogo}
                                                        loadingText="Uploading"
                                                    >
                                                        {schoolDetails.logoPreview ? "Replace" : "Upload"}
                                                    </Button>
                                                    {schoolDetails.logoPreview && (
                                                        <IconButton
                                                            size="sm"
                                                            icon={<DeleteIcon/>}
                                                            aria-label="Remove school logo"
                                                            variant="ghost"
                                                            colorScheme="red"
                                                            onClick={removeLogo}
                                                        />
                                                    )}
                                                </HStack>
                                                <VisuallyHidden>
                                                    <Input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleLogoChange}
                                                    />
                                                </VisuallyHidden>
                                            </Box>
                                        </Flex>
                                    </Box>

                                    <FormControl>
                                        <FormLabel fontSize="sm" fontWeight="semibold">Description</FormLabel>
                                        <Textarea
                                            name="description"
                                            value={schoolDetails.description}
                                            onChange={handleInputChange}
                                            placeholder="Add short notes about the school, campus, or onboarding needs"
                                            minH="126px"
                                            resize="vertical"
                                            bg={inputBg}
                                            borderColor={borderColor}
                                        />
                                        <FormHelperText>Optional notes for admin reference.</FormHelperText>
                                    </FormControl>
                                </Stack>
                            </Box>
                        </SimpleGrid>

                        <Box borderWidth="1px" borderColor={borderColor} borderRadius="14px" p={{base: 4, md: 5}} bg={sectionBg}>
                            <Flex align="center" justify="space-between" mb={5} gap={3} wrap="wrap">
                                <Box>
                                    <Text fontWeight="bold" color="gray.700">Services</Text>
                                    <Text fontSize="sm" color={mutedText}>
                                        Select the profile menus (services) enabled for this school.
                                    </Text>
                                </Box>
                                <Icon as={FiGrid} color="blue.400" boxSize={5}/>
                            </Flex>

                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="semibold">Profile Menus</FormLabel>
                                <Menu closeOnSelect={false} matchWidth>
                                    <MenuButton
                                        as={Button}
                                        rightIcon={isLoadingProfileMenus ? <Spinner size="xs"/> : <ChevronDownIcon/>}
                                        variant="outline"
                                        w="100%"
                                        textAlign="left"
                                        fontWeight="normal"
                                        bg={inputBg}
                                        borderColor={borderColor}
                                        isDisabled={isLoadingProfileMenus && profileMenus.length === 0}
                                    >
                                        {schoolDetails.profileMenuCodes.length > 0
                                            ? `${schoolDetails.profileMenuCodes.length} selected`
                                            : (isLoadingProfileMenus ? "Loading services..." : "Select services")}
                                    </MenuButton>
                                    <MenuList maxH="260px" overflowY="auto">
                                        {profileMenus.length === 0 && !isLoadingProfileMenus ? (
                                            <Box px={4} py={2}>
                                                <Text fontSize="sm" color={mutedText}>No services available.</Text>
                                            </Box>
                                        ) : (
                                            <MenuOptionGroup
                                                type="checkbox"
                                                value={schoolDetails.profileMenuCodes}
                                                onChange={handleProfileMenusChange}
                                            >
                                                {profileMenus.map((menu) => {
                                                    const code = menu.value ?? menu.code;
                                                    return (
                                                        <MenuItemOption key={code} value={code}>
                                                            {menu.label ?? menu.name ?? code}
                                                        </MenuItemOption>
                                                    );
                                                })}
                                            </MenuOptionGroup>
                                        )}
                                    </MenuList>
                                </Menu>
                                <FormHelperText>Choose one or more services to attach to the school.</FormHelperText>

                                {schoolDetails.profileMenuCodes.length > 0 && (
                                    <Wrap mt={3} spacing={2}>
                                        {schoolDetails.profileMenuCodes.map((code) => (
                                            <WrapItem key={code}>
                                                <Tag borderRadius="full" variant="subtle" colorScheme="blue">
                                                    <TagLabel>{profileMenuLabel(code)}</TagLabel>
                                                    <TagCloseButton onClick={() => removeProfileMenu(code)}/>
                                                </Tag>
                                            </WrapItem>
                                        ))}
                                    </Wrap>
                                )}
                            </FormControl>
                        </Box>
                    </Stack>
                </ModalBody>

                <Divider/>

                <ModalFooter px={{base: 4, md: 8}} py={4}>
                    <Flex w="100%" gap={3} justify="space-between" align="center" direction={{base: "column", sm: "row"}}>
                        <HStack color={completionPercent === 100 ? "green.500" : mutedText} spacing={2}>
                            <Icon as={completionPercent === 100 ? CheckIcon : CloseIcon} boxSize={3}/>
                            <Text fontSize="sm">{completedRequiredFields}/{requiredFields.length} required fields complete</Text>
                        </HStack>

                        <HStack spacing={3} w={{base: "100%", sm: "auto"}}>
                            <Button variant="ghost" onClick={onClose} w={{base: "50%", sm: "auto"}} isDisabled={isSubmitting}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={handleSubmit}
                                w={{base: "50%", sm: "auto"}}
                                isLoading={isSubmitting}
                                isDisabled={isUploadingLogo}
                            >
                                {isEdit ? "Update School" : "Save School"}
                            </Button>
                        </HStack>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default AddSchoolModal;
